import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  ActivityIndicator,
  Alert,
  Image,
  KeyboardAvoidingView
} from 'react-native';
import { router } from 'expo-router';
import { observer } from '@legendapp/state/react';
import { Ionicons } from '@expo/vector-icons';
import { AppAuthLoginLogger } from '../../utils/logger';
import theme from '../../config/theme';
import { logout } from '../../services/authService';
import { APP_CONSTANTS } from '../../config/constants';
import { supabase } from '../../services/supabase';
import { databaseService } from '../../services/databaseService';
import { globalState, userActions, UserProfile } from '../../state/globalState';
import { useWebAlert } from '../../components/WebAlert';
import FormFieldError from '../../components/FormFieldError';
import WebLoadingOverlay from '../../components/WebLoadingOverlay';
import * as ImagePicker from 'expo-image-picker';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { uploadUserProfileImage } from '../../services/userService';
import FormattedInput from '../../components/FormattedInput';
import { debugPause } from '@/utils/debugPause';
import { pickImageFromLibrary, takePhotoFromCamera } from '@/utils/media/imagePicker';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getAndroidActionSheetSafeAreaStyle } from '@/utils/safeArea';

// Logger específico para esta tela
const logger = AppAuthLoginLogger.Register;

// Erros do formulário
interface FormErrors {
  name: string | null;
  email: string | null;
  telefone: string | null;
  general: string | null;
}

const RegisterUserScreen = observer(() => {
  // Estado do usuário atual
  const userId = globalState.user.user.get()?.id;
  const empresaId = globalState.company.data.get()?.id;
  const userEmail = globalState.user.user.get()?.email;

  // Estado de formulário do usuário
  const [userData, setUserData] = useState<Partial<UserProfile>>({
    id: userId || '',
    name: '',
    email: userEmail || '', // Pré-preencher com email da autenticação
    telefone: '',
    empresa_id: empresaId || '',
  });

  // Estado de erros do formulário
  const [formErrors, setFormErrors] = useState<FormErrors>({
    name: null,
    email: null,
    telefone: null,
    general: null
  });

  // Estados para controle do componente
  const [isLoading, setIsLoading] = useState(false);
  const [photoUri, setPhotoUri] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const { showAlert } = useWebAlert();
  const { showActionSheetWithOptions } = useActionSheet();
  const safeAreaInsets = useSafeAreaInsets();

  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK' }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg);
    }
  };

  // Validações de campos
  const validateName = (value?: string): boolean => {
    if (!value?.trim()) {
      setFormErrors(prev => ({ ...prev, name: 'Nome é obrigatório' }));
      return false;
    }
    setFormErrors(prev => ({ ...prev, name: null }));
    return true;
  };

  const validateEmail = (value?: string): boolean => {
    if (!value?.trim()) {
      setFormErrors(prev => ({ ...prev, email: 'Email é obrigatório' }));
      return false;
    } else if (!/\S+@\S+\.\S+/.test(value)) {
      setFormErrors(prev => ({ ...prev, email: 'Email inválido' }));
      return false;
    }
    setFormErrors(prev => ({ ...prev, email: null }));
    return true;
  };

  const validateForm = (): boolean => {
    const isNameValid = validateName(userData.name);
    const isEmailValid = validateEmail(userData.email);

    return isNameValid && isEmailValid;
  };

  // Função para selecionar imagem da foto de perfil
  const handlePickImage = () => {
    showActionSheetWithOptions(
      {
        options: ['Cancelar', 'Tirar foto', 'Galeria'],
        cancelButtonIndex: 0,
        title: 'Selecionar imagem',
        message: 'De onde você quer obter sua foto de perfil?',
        titleTextStyle: {
          color: theme.colors.text.primary,
          fontSize: theme.fontSizes.md,
          fontWeight: theme.fontWeights.medium as any,
        },
        messageTextStyle: {
          color: theme.colors.text.secondary,
          fontSize: theme.fontSizes.sm,
        },
        containerStyle: {
          backgroundColor: theme.colors.background.primary,
          ...getAndroidActionSheetSafeAreaStyle({
            bottomInset: safeAreaInsets.bottom,
          }),
        },
        textStyle: {
          color: theme.colors.primary.default,
          fontSize: theme.fontSizes.md,
        },
      },
      async (buttonIndex) => {
        if (buttonIndex === 1) {
          // câmera
          await takePhoto();
        }
        if (buttonIndex === 2) {
          // galeria
          await pickFromLibrary();
        }
      }
    );
  };

  const takePhoto = async () => {
    try {
      const outcome = await takePhotoFromCamera({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.7,
      }, {
        source: 'auth_register_user_photo',
        screen: 'auth/register-user',
        action: 'take_user_photo_with_camera',
        entityType: 'user_profile_photo',
        entityId: userId ?? null,
        empresaId: empresaId ?? null,
      });

      if (outcome.status === 'denied') {
        showMessage('Permissão negada', 'Precisamos de permissão para acessar a câmera.');
        return;
      }

      if (outcome.status === 'failed') {
        logger.error('Erro ao tirar foto:', outcome.error);
        showMessage('Erro', 'Não foi possível tirar a foto.');
        return;
      }

      if (outcome.status === 'success' && outcome.result.assets?.length > 0) {
        const selectedImage = outcome.result.assets[0];
        setPhotoUri(selectedImage.uri);
      }
    } catch (error) {
      logger.error('Erro ao tirar foto:', error);
      showMessage('Erro', 'Não foi possível tirar a foto.');
    }
  };

  const pickFromLibrary = async () => {
    try {
      const outcome = await pickImageFromLibrary({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.7,
      }, {
        source: 'auth_register_user_photo',
        screen: 'auth/register-user',
        action: 'pick_user_photo_from_library',
        entityType: 'user_profile_photo',
        entityId: userId ?? null,
        empresaId: empresaId ?? null,
      });

      if (outcome.status === 'denied') {
        showMessage('Permissão negada', 'Precisamos de permissão para acessar suas fotos.');
        return;
      }

      if (outcome.status === 'failed') {
        logger.error('Erro ao selecionar imagem da galeria:', outcome.error);
        showMessage('Erro', 'Não foi possível selecionar a imagem.');
        return;
      }

      if (outcome.status !== 'success' || !outcome.result.assets?.length) {
        return;
      }

      const selectedImage = outcome.result.assets[0];
      setPhotoUri(selectedImage.uri);
    } catch (error) {
      logger.error('Erro ao selecionar imagem da galeria:', error);
      showMessage('Erro', 'Não foi possível selecionar a imagem.');
    }
  };

  // Função para registrar o usuário
  const handleRegisterUser = async () => {
    if (!validateForm()) {
      setFormErrors(prev => ({ ...prev, general: 'Por favor, corrija os erros no formulário.' }));
      return;
    }

    if (!userId) {
      logger.error('ID de usuário não encontrado');
      showMessage('Erro', 'Usuário não identificado. Por favor, faça login novamente.');
      return;
    }

    setIsLoading(true);
    setFormErrors(prev => ({ ...prev, general: null }));

    try {
      // Preparar dados do usuário
      const userPayload = {
        id: userId,
        name: userData.name || '',
        email: userData.email || '',
        telefone: userData.telefone || null,
        empresa_id: empresaId || null,
        user_type: 'admin',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // 1. Criar usuário no banco de dados local e no Supabase
      logger.info('Registrando usuário no banco local e no Supabase');

      // Inserir no Supabase
      const { data: createdUser, error: userError } = await supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME)
        .insert(userPayload)
        .select()
        .single();

      if (userError) {
        if (userError.message.includes('duplicate key') || userError.message.includes('unique constraint')) {
          // Caso o usuário já exista, fazer update em vez de insert
          logger.info('Usuário já existe, atualizando dados...');
          const { data: updatedUser, error: updateError } = await supabase
            .from(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME)
            .update({
              name: userData.name,
              telefone: userData.telefone,
              empresa_id: empresaId
            })
            .eq('id', userId)
            .select()
            .single();

          if (updateError) {
            logger.error('Erro ao atualizar usuário no Supabase:', updateError);
            throw new Error(`Erro ao atualizar usuário: ${updateError.message}`);
          }

          // Usar os dados atualizados
          const userToSave = updatedUser;

          // Inserir no banco local
          const localSuccess = await databaseService.updateItem(
            APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
            userId,
            {
              name: userData.name,
              telefone: userData.telefone,
              empresa_id: empresaId
            },
            true // skipPendingOperation = true
          );

          if (!localSuccess) {
            logger.warn('Falha ao atualizar usuário no banco local, continuando...');
          }
        } else {
          logger.error('Erro ao registrar usuário no Supabase:', userError);
          throw new Error(`Erro ao registrar usuário: ${userError.message}`);
        }
      } else {
        // Se o registro foi bem-sucedido, inserir no banco local
        const localSuccess = await databaseService.createItem(
          APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
          createdUser,
          true // skipPendingOperation = true
        );

        if (!localSuccess) {
          logger.warn('Falha ao inserir usuário no banco local, continuando...');
        }
      }

      // 2. Fazer upload da foto de perfil se existir
      let photoUploadResult = null;
      if (photoUri) {
        logger.info('Foto encontrada, iniciando upload');
        setUploadingPhoto(true);
        try {
          logger.info(`Iniciando upload da foto para usuário ${userId}`);
          photoUploadResult = await uploadUserProfileImage(userId, photoUri);
          if (!photoUploadResult.success) {
            throw new Error(photoUploadResult.error);
          }
          logger.info('Upload da foto concluído com sucesso');
        } catch (photoError) {
          logger.error('Erro no upload da foto:', photoError);
          showMessage('Aviso', 'O cadastro foi concluído, mas houve um problema ao fazer upload da foto. Você pode adicioná-la mais tarde.');
        } finally {
          setUploadingPhoto(false);
        }
      } else {
        logger.info('Não há foto para upload');
      }

      // 3. Atualizar o estado global do usuário
      userActions.setProfile({
        id: userId,
        name: userData.name,
        email: userData.email,
        telefone: userData.telefone || undefined,
        photo_url: photoUploadResult?.success ? photoUploadResult.data?.publicURL : undefined,
        empresa_id: empresaId,
        created_at: new Date().toISOString()
      });      
      
      

      // 5. Mostrar mensagem de sucesso e navegar para o app
      showMessage('Sucesso', 'Perfil de usuário criado com sucesso!');
      logger.info('Perfil de usuário criado com sucesso; atualizando estado e navegando para /(app)');

      // 6. Marcar que o usuário existe no banco
      userActions.setUserExistsInDatabase(true);
      logger.info('Flag existsInDatabase atualizada para true; chamando router.replace("/(app)")');
      router.replace('/(app)');
      
    } catch (error) {
      logger.error('Erro ao registrar usuário:', error);

      let errorMessage = 'Ocorreu um erro ao registrar o usuário.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      setFormErrors(prev => ({ ...prev, general: errorMessage }));
      showMessage('Erro', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    logger.debug('Fazendo logout a partir da tela de registro de usuário');
    await logout();
    logger.info('Logout concluído; redirecionando usuário para /(auth)/login');
    router.replace('/(auth)/login');
  };

  const handleInputChange = (field: keyof FormErrors, value: string) => {
    setUserData(prev => ({ ...prev, [field]: value }));

    // Validar campo específico conforme alterado
    switch (field) {
      case 'name':
        validateName(value);
        break;
      case 'email':
        validateEmail(value);
        break;
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 64 : 0}
    >
      <WebLoadingOverlay
        visible={isLoading}
        message="Registrando usuário..."
        hint="Por favor, aguarde enquanto finalizamos seu perfil. Esse processo pode levar alguns instantes."
      />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <Text style={styles.title}>Cadastrar Usuário</Text>
          <Text style={styles.subtitle}>
            Complete seu perfil para continuar
          </Text>
        </View>

        {/* Foto do usuário */}
        <View style={styles.photoContainer}>
          <TouchableOpacity
            style={styles.photoWrapper}
            onPress={handlePickImage}
            disabled={uploadingPhoto}
          >
            {photoUri ? (
              <Image source={{ uri: photoUri }} style={styles.photoImage} />
            ) : (
              <View style={styles.photoPlaceholder}>
                <Ionicons name="person" size={50} color={theme.colors.text.inverse} />
              </View>
            )}
            {uploadingPhoto && (
              <View style={styles.uploadingOverlay}>
                <ActivityIndicator size="large" color={theme.colors.primary.default} />
              </View>
            )}
          </TouchableOpacity>
          <Text style={styles.photoText}>Foto de Perfil (opcional)</Text>
          <TouchableOpacity
            style={styles.photoButton}
            onPress={handlePickImage}
            disabled={uploadingPhoto}
          >
            <Text style={styles.photoButtonText}>
              {photoUri ? 'Alterar Foto' : 'Adicionar Foto'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Formulário */}
        <View style={styles.formContainer}>
          <Text style={styles.formSectionTitle}>Informações Pessoais</Text>

          {/* Nome do Usuário - Campo obrigatório */}
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Nome *</Text>
            <TextInput
              style={[
                styles.input,
                formErrors.name ? styles.inputError : null
              ]}
              placeholder="Digite seu nome completo"
              placeholderTextColor={theme.colors.text.tertiary}
              value={userData.name}
              onChangeText={(text) => handleInputChange('name', text)}
            />
            <FormFieldError message={formErrors.name} />
          </View>

          {/* Email - Campo obrigatório, mas pré-preenchido e talvez não editável */}
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Email *</Text>
            <TextInput
              style={[
                styles.input,
                formErrors.email ? styles.inputError : null
              ]}
              placeholder="email@exemplo.com"
              placeholderTextColor={theme.colors.text.tertiary}
              value={userData.email}
              onChangeText={(text) => handleInputChange('email', text)}
              keyboardType="email-address"
              autoCapitalize="none"
              editable={!userEmail} // Se já tiver email da autenticação, não deixar editar
            />
            <FormFieldError message={formErrors.email} />
          </View>

          {/* Telefone */}
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Telefone</Text>
            <FormattedInput
              type="phone"
              style={styles.input}
              placeholder="Digite apenas números"
              placeholderTextColor={theme.colors.text.tertiary}
              value={userData.telefone || ''}
              onChangeText={(text) => handleInputChange('telefone', text)}
            />
          </View>

          {/* Erro geral do formulário */}
          {formErrors.general && (
            <Text style={styles.generalError}>{formErrors.general}</Text>
          )}

          {/* Botões de ação */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.secondaryButton]}
              onPress={handleSignOut}
              disabled={isLoading}
            >
              <Text style={styles.secondaryButtonText}>Sair</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.primaryButton, isLoading ? styles.disabledButton : null]}
              onPress={handleRegisterUser}
              disabled={isLoading}
            >
              {isLoading ? (
                <ActivityIndicator size="small" color={theme.colors.text.inverse} />
              ) : (
                <Text style={styles.primaryButtonText}>Finalizar Cadastro</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: theme.spacing.xl,
  },
  header: {
    paddingHorizontal: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.md : theme.spacing.xl,
    paddingBottom: theme.spacing.lg,
    backgroundColor: theme.colors.primary.default,
  },
  title: {
    fontSize: theme.fontSizes.xxl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.inverse,
    textAlign: 'center',
    marginTop: theme.spacing.xs,
    opacity: 0.9,
  },
  photoContainer: {
    alignItems: 'center',
    paddingVertical: theme.spacing.lg,
    backgroundColor: theme.colors.background.primary,
    marginBottom: theme.spacing.md,
  },
  photoWrapper: {
    width: 120,
    height: 120,
    borderRadius: 60,
    overflow: 'hidden',
    marginBottom: theme.spacing.sm,
  },
  photoImage: {
    width: '100%',
    height: '100%',
  },
  photoPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.colors.primary.light,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 60,
  },
  uploadingOverlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.sm,
  },
  photoButton: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.sm,
  },
  photoButtonText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
  },
  formContainer: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    margin: theme.spacing.md,
    ...theme.shadows.md,
  },
  formSectionTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm,
    marginTop: theme.spacing.xs,
  },
  inputContainer: {
    marginBottom: theme.spacing.md,
  },
  label: {
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.xs,
  },
  input: {
    backgroundColor: theme.colors.background.secondary,
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: Platform.OS === 'ios' ? theme.spacing.sm : theme.spacing.xs,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
  },
  inputError: {
    borderColor: theme.colors.util.error,
  },
  generalError: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.sm,
    textAlign: 'center',
    marginVertical: theme.spacing.sm,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.spacing.lg,
  },
  primaryButton: {
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.sm,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
    flex: 3,
  },
  secondaryButton: {
    borderColor: theme.colors.border.default,
    borderWidth: 1,
    borderRadius: theme.borderRadius.sm,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
    flex: 1,
  },
  disabledButton: {
    opacity: 0.7,
  },
  primaryButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
  secondaryButtonText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
});

export default RegisterUserScreen; 
